import { useEffect, useRef } from 'preact/hooks'
import { signalRService } from '@/services/signalRService'
import type { WebRoomRecordingChangedEventDto } from '@/api/webinarRoom'
import { WebinarRoomEvents } from '@/webinarRoom/events'

/** Sent on the dashboard's own `orbitalx` hub, unlike the room's other events. */
const WEB_ROOM_RECORDING_CHANGED = WebinarRoomEvents.WebRoomRecordingChanged

/**
 * Rereads the web when its room recording changes state: uploaded, prepared, ready, failed, replaced (ORB-1087).
 * The payload says only which web; the web's GET is the truth, and the sign-off list moves with it.
 *
 * On `workspace-{id}` because that is the only group the subscribe endpoint access-checks; the WebId filter
 * narrows it, since every web in the workspace shares the group.
 */
export function useRoomRecordingLive(workspaceId: string, webId: string, onChange: () => void): void {
    const onChangeRef = useRef(onChange)
    onChangeRef.current = onChange

    useEffect(() => {
        if (!workspaceId || !webId) return
        const group = `workspace-${workspaceId}`
        let cancelled = false
        let joined = false

        const onEvent = (payload: unknown) => {
            const event = payload as WebRoomRecordingChangedEventDto | null
            if (event?.WebId === webId) onChangeRef.current()
        }
        const leave = () => {
            if (!joined) return
            joined = false
            void signalRService.leaveGroup(group).catch(() => { /* Teardown may race a dropped connection. */ })
        }

        void signalRService.joinGroupWithStatus(group).then(subscribed => {
            joined = subscribed
            if (cancelled) { leave(); return }
            if (subscribed) signalRService.on(WEB_ROOM_RECORDING_CHANGED, onEvent)
        }).catch(() => {
            /* Without the connection the card still rereads after every change it makes itself. */
        })

        return () => {
            cancelled = true
            signalRService.off(WEB_ROOM_RECORDING_CHANGED, onEvent)
            leave()
        }
    }, [workspaceId, webId])
}
